import { useState } from 'react';
import { PageSizeOption, useAlertNoticesQuery } from '@/graphql/generated';
import { Pagination } from '../molecules';
import { LoadingSpinner } from '../atoms';
import { useUserLanguage, useErrorHandlerContext } from '@/customHooks';
import { formatAlertRentPeriod } from '@/helpers/formatAlertRentPeriod';
import textsData from '@/textData/reportTexts.json';

const PAGE_SIZE = 25;

const readableDueDate = (
  value: unknown,
  lang: 'es' | 'en',
): string => {
  if (value == null) return '-';
  try {
    const dateObj = typeof value === 'string' ? new Date(value) : (value as Date);
    if (Number.isNaN(dateObj.getTime())) return '-';
    return dateObj.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

export const AlertNoticesReportPage = () => {
  const userLanguage = useUserLanguage();
  const lang = userLanguage === 'es' ? 'es' : 'en';
  const texts = (lang === 'es' ? textsData.es : textsData.en).alertNoticesReport;
  const { open: errorHandle } = useErrorHandlerContext();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, loading, error } = useAlertNoticesQuery({
    variables: {
      page: currentPage,
      pageSize: PAGE_SIZE,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  if (error) errorHandle(error);

  const items = data?.alertNotices?.items ?? [];
  const pagination = data?.alertNotices?.pagination;
  const totalPages = Math.max(1, pagination?.totalPages ?? 1);
  const currentDataCount = items.length;
  const showPagination = currentDataCount > 0 || (pagination?.totalItems ?? 0) > 0;

  return (
    <>
      <div className="w-full px-4 md:px-6 space-y-6">
        <h2 className="text-xl md:text-2xl font-semibold">{texts.title}</h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner text />
          </div>
        ) : items.length === 0 ? (
          <div className="w-full py-4 text-center">
            <span className="font-bold">{texts.noData.table}</span>
          </div>
        ) : (
          <div className="pt-4 pb-4 rounded-xl grid grid-cols-[1.2fr_1.3fr_1.1fr_2fr] overflow-x-auto min-w-[920px]">
            <div className="contents">
              <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
                {texts.columns.tenant}
              </div>
              <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
                {texts.columns.period}
              </div>
              <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
                {texts.columns.dueDate}
              </div>
              <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
                {texts.columns.message}
              </div>
            </div>

            {items.map((row, index) => {
              const rowBg = index % 2 !== 0 ? 'bg-background2' : 'bg-white';
              const periodLabel = formatAlertRentPeriod(row.period, lang);
              const messageLine = `${row.tenantName} ${texts.messageTemplate} ${periodLabel}`;
              return (
                <div key={row.id} className="contents">
                  <div
                    className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center ${rowBg}`}
                  >
                    {row.tenantName}
                  </div>
                  <div
                    className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center ${rowBg}`}
                  >
                    {periodLabel}
                  </div>
                  <div
                    className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center ${rowBg}`}
                  >
                    {readableDueDate(row.dueDate, lang)}
                  </div>
                  <div
                    className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center ${rowBg}`}
                  >
                    {messageLine}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showPagination && !loading && (
          <div className="pt-2">
            <Pagination
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              currentDataCount={currentDataCount}
              pageSize={PageSizeOption.TwentyFive}
              totalPages={totalPages}
            />
          </div>
        )}
      </div>
    </>
  );
};
